/**
 * ELF-CIRCLE social — section `#9c`, "One hub, every channel".
 *
 * Platform tabs split the feed so the grid never mixes formats. Each platform
 * owns its own set of posts: a featured tile plus a six-up grid, and every one
 * of those has its own image, caption, stats and redirect link — all editable
 * from the page when signed in as an admin.
 *
 * The design reused a single set of photos across all four tabs; that was a
 * mock. Here each tab is genuinely its own channel.
 */

import React, { useState } from 'react';
import EditableImage from './EditableImage';
import EditableText from './EditableText';
import { useLandingImages } from './useLandingImages';
import { SOCIAL_PLATFORMS, SOCIAL_POSTS, type SocialPlatform, type SocialPostId } from './landingImages';

interface PostDefaults {
  caption: string;
  likes: string;
  comments: string;
}

interface PlatformDefaults {
  label: string;
  followers: string;
  handle: string;
  /** Default destination for every post on this channel. */
  url: string;
  posts: Record<SocialPostId, PostDefaults>;
}

const post = (caption: string, likes: string, comments: string): PostDefaults => ({
  caption,
  likes,
  comments,
});

const PLATFORMS: Record<SocialPlatform, PlatformDefaults> = {
  instagram: {
    label: 'Instagram',
    followers: '142k',
    handle: '@elfcircle',
    url: 'https://instagram.com/elfcircle',
    posts: {
      featured: post('Backstage, Neon Circuit', '12.4k', '318'),
      post1: post('Backstage, Neon Circuit', '12.4k', '318'),
      post2: post('Build week 03', '4.1k', '92'),
      post3: post('Cut & Thread fitting', '9.8k', '241'),
      post4: post('Runway rehearsal', '7.2k', '164'),
      post5: post('Frame Rate lineup', '15.1k', '402'),
      post6: post('Block Party crew', '6.6k', '137'),
    },
  },
  tiktok: {
    label: 'TikTok',
    followers: '88k',
    handle: '@elf.street',
    url: 'https://tiktok.com/@elfcircle',
    posts: {
      featured: post('Block Party crew', '31.2k', '806'),
      post1: post('Soundcheck, 30 seconds', '22.4k', '512'),
      post2: post('Fit check — Milan', '18.9k', '431'),
      post3: post('Crowd from the booth', '27.1k', '664'),
      post4: post('Backstage sprint', '11.3k', '208'),
      post5: post('One take, no edits', '9.7k', '175'),
      post6: post('Load-in timelapse', '14.8k', '299'),
    },
  },
  youtube: {
    label: 'YouTube',
    followers: '31k',
    handle: '@elfcircle',
    url: 'https://youtube.com/@elfcircle',
    posts: {
      featured: post('Frame Rate — festival recap', '8.4k', '211'),
      post1: post('Neon Circuit, full set', '6.2k', '148'),
      post2: post('How the runway was built', '3.9k', '87'),
      post3: post('Director Q&A, uncut', '2.7k', '64'),
      post4: post('Season trailer', '11.5k', '312'),
      post5: post('Artist residency, ep. 1', '4.4k', '96'),
      post6: post('Behind the drop', '5.1k', '119'),
    },
  },
  x: {
    label: 'X',
    followers: '19k',
    handle: '@elf.fashion',
    url: 'https://x.com/elfcircle',
    posts: {
      featured: post('Runway rehearsal', '2.1k', '58'),
      post1: post('Line-up announced', '3.4k', '121'),
      post2: post('Tickets live at noon', '1.8k', '44'),
      post3: post('Milan, we are here', '2.6k', '77'),
      post4: post('Thread: building the season', '4.9k', '186'),
      post5: post('Last release, 40 left', '1.2k', '31'),
      post6: post('See you in London', '2.9k', '92'),
    },
  },
};

/** The always-on channel chips under the grid. */
const LINKS: { id: string; label: string; name: string; url: string }[] = [
  { id: 'ig', label: 'IG', name: 'Instagram', url: 'https://instagram.com/elfcircle' },
  { id: 'tt', label: 'TT', name: 'TikTok', url: 'https://tiktok.com/@elfcircle' },
  { id: 'yt', label: 'YT', name: 'YouTube', url: 'https://youtube.com/@elfcircle' },
  { id: 'x', label: 'X', name: 'X', url: 'https://x.com/elfcircle' },
  { id: 'in', label: 'IN', name: 'LinkedIn', url: 'https://linkedin.com/company/elfcircle' },
];

const GRID_POSTS = SOCIAL_POSTS.filter((p) => p !== 'featured');

export const ElfSocial: React.FC = () => {
  const { text, canEdit } = useLandingImages();
  const [tab, setTab] = useState<SocialPlatform>('instagram');
  const platform = PLATFORMS[tab];

  /** Where a post sends the visitor; falls back to the channel's own URL. */
  const linkFor = (id: SocialPostId) => text(`social.${tab}.${id}.link`, platform.url);

  /**
   * Admins get a div, visitors an anchor. In edit mode the tile contains
   * buttons, and a button inside an anchor is invalid — the anchor swallows
   * the click and navigates away mid-edit.
   */
  const tileProps = (id: SocialPostId) =>
    canEdit
      ? { as: 'div' as const, extra: {} }
      : {
          as: 'a' as const,
          extra: {
            href: linkFor(id),
            target: '_blank',
            rel: 'noopener noreferrer' as const,
          },
        };

  const renderTile = (id: SocialPostId, className: string) => {
    const { as: Tag, extra } = tileProps(id);
    const defaults = platform.posts[id];
    return (
      <Tag key={`${tab}-${id}`} className={`post ${className}`} {...extra}>
        <EditableImage
          slot={`social.${tab}.${id}`}
          alt=""
          linkKey={`social.${tab}.${id}.link`}
          linkFallback={platform.url}
        />
        <div className="postov">
          {id === 'featured' && (
            <EditableText
              as="div"
              contentKey={`social.${tab}.handle`}
              fallback={platform.handle}
              label="Channel handle"
              className="elf-social__feat-handle"
            />
          )}
          <EditableText
            as="b"
            contentKey={`social.${tab}.${id}.caption`}
            fallback={defaults.caption}
            label="Caption"
          />
          <div className="pstat">
            <span>
              ♥{' '}
              <EditableText
                contentKey={`social.${tab}.${id}.likes`}
                fallback={defaults.likes}
                label="Likes"
              />
            </span>
            <span>
              💬{' '}
              <EditableText
                contentKey={`social.${tab}.${id}.comments`}
                fallback={defaults.comments}
                label="Comments"
              />
            </span>
          </div>
        </div>
      </Tag>
    );
  };

  return (
    <section id="social" className="elf-band elf-social">
      <EditableImage slot="social.backdrop" className="elf-social__bg" alt="" />
      <div className="elf-social__scrim" />
      <div className="sigrule elf-band__rule" />
      <div className="elf-band__inner">
        <div className="elf-section__head" style={{ marginBottom: 30 }}>
          <div style={{ maxWidth: 620 }}>
            <span className="eyebrow an" style={{ animationDelay: '.05s' }}>
              <i />
              <EditableText contentKey="social.eyebrow" fallback="Social" label="Eyebrow" />
            </span>
            <EditableText
              as="h2"
              contentKey="social.title"
              fallback="One hub, every channel"
              label="Section title"
              className="an elf-section__title"
              style={{ animationDelay: '.12s' }}
            />
            <EditableText
              as="p"
              contentKey="social.lede"
              fallback="Tabs split the feed by platform so the grid never mixes formats. The featured post sits beside it with the follower count for that channel."
              label="Section intro"
              multiline
              className="an elf-section__lede"
              style={{ animationDelay: '.18s' }}
            />
          </div>
        </div>

        <div className="elf-social__tabs">
          {SOCIAL_PLATFORMS.map((id) => (
            <button
              key={id}
              type="button"
              className={id === tab ? 'ptab on' : 'ptab'}
              aria-pressed={id === tab}
              onClick={() => setTab(id)}
            >
              {text(`social.${id}.label`, PLATFORMS[id].label)}
            </button>
          ))}
          <div style={{ flex: 1 }} />
          <div className="elf-social__followers">
            <EditableText
              as="b"
              contentKey={`social.${tab}.followers`}
              fallback={platform.followers}
              label="Follower count"
            />{' '}
            followers
          </div>
        </div>

        <div className="elf-social__layout">
          {renderTile('featured', 'elf-social__feat')}

          <div>
            <div className="elf-social__grid" key={tab}>
              {GRID_POSTS.map((id) => renderTile(id, 'elf-social__tile'))}
            </div>

            <div className="elf-social__foot">
              <EditableText
                as="div"
                contentKey="social.note"
                fallback="Posts refresh from the live feed."
                label="Footer note"
              />
              <div className="elf-social__links">
                {LINKS.map((l) => (
                  <a
                    key={l.id}
                    className="plat"
                    href={text(`social.link.${l.id}`, l.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={l.name}
                    aria-label={l.name}
                  >
                    {l.label}
                  </a>
                ))}
              </div>
              {/* Follow goes to the channel currently selected by the tabs. */}
              <a
                className="pillcta mag"
                href={platform.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <EditableText contentKey="social.cta" fallback="Follow" label="Button label" />{' '}
                <span>→</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ElfSocial;
